import { useReducedMotion } from 'animations/useReducedMotion';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, ReactNode, useState } from 'react';

export const DETAIL_TEXT_PREVIEW_LEN = 72;

interface ExpandableDetailTextProps {
    value: string;
    href?: string;
    linkClassName?: string;
}

/** Truncated preview + "Show full text" — used in row detail panels (AS2, SFTP, etc.). */
const ExpandableDetailText: FC<ExpandableDetailTextProps> = ({ value, href, linkClassName }) => {
    const reducedMotion = useReducedMotion();
    const [expanded, setExpanded] = useState(false);
    const needsExpand = value.length > DETAIL_TEXT_PREVIEW_LEN;
    const preview = `${value.slice(0, DETAIL_TEXT_PREVIEW_LEN)}…`;

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded((v) => !v);
    };

    const body = (text: string, wrap: boolean): ReactNode => {
        if (href && wrap) {
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClassName || 'row-detail-panel__link row-detail-panel__value--wrap break-all'}
                >
                    {text}
                </a>
            );
        }
        return (
            <span
                className={
                    wrap
                        ? 'row-detail-panel__value row-detail-panel__value--wrap break-all'
                        : 'row-detail-panel__value row-detail-panel__value--truncate'
                }
            >
                {text}
            </span>
        );
    };

    if (!needsExpand) {
        return body(value, true);
    }

    if (reducedMotion) {
        return (
            <div className="row-detail-panel__long-text">
                {body(expanded ? value : preview, expanded)}
                <button type="button" className="row-detail-panel__expand-btn" onClick={toggle}>
                    {expanded ? 'Show less' : 'Show full text'}
                </button>
            </div>
        );
    }

    return (
        <div className="row-detail-panel__long-text">
            <AnimatePresence initial={false} mode="wait">
                {expanded ? (
                    <motion.div
                        key="full"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                    >
                        {body(value, true)}
                    </motion.div>
                ) : (
                    <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        {body(preview, false)}
                    </motion.div>
                )}
            </AnimatePresence>
            <button type="button" className="row-detail-panel__expand-btn" onClick={toggle}>
                {expanded ? 'Show less' : 'Show full text'}
            </button>
        </div>
    );
};

export function isWideDetailField(field: { type: string; value: string }): boolean {
    return field.type === 'long-text' || field.value.length > DETAIL_TEXT_PREVIEW_LEN;
}

export default ExpandableDetailText;
